import { ChangeDetectorRef, Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core'
import { TranslateService, TranslateModule } from '@ngx-translate/core'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { NotificationType } from 'angular2-notifications'
import { ImageCroppedEvent, ImageTransform, ImageCropperComponent } from 'ngx-image-cropper'
import { $app } from '../../../../app.service'
import { AppDialogDeleteComponent } from '../../../../shared/components/dialogs/app-dialog-delete/app-dialog-delete.component'
import { AppBasicFile } from '../../../../shared/models/interfaces/app-basic-file.interface'
import { AppNotificationService } from '../../../../shared/services/app-notification.service'
import { DialogModule } from 'primeng/dialog'
import { TooltipModule } from 'primeng/tooltip'
import { ButtonModule } from 'primeng/button'
import { AppBasicLoaderComponent } from '../../../../shared/components/app-basic-loader/app-basic-loader.component'

@Component({
   selector: 'ep-running-logo',
   templateUrl: './ep-running-logo.component.html',
   styleUrls: ['./ep-running-logo.component.scss'],
   standalone: true,
   imports: [AppBasicLoaderComponent, ButtonModule, TooltipModule, ImageCropperComponent, DialogModule, TranslateModule]
})
export class EpRunningLogoComponent {
   @Input() logo?: string | null
   @Input() isLoadingLogo?: boolean | null

   showCropper: boolean = false

   croppedImageFile?: AppBasicFile

   scale: number = 1

   transform: ImageTransform = {}
   imageChangedEvent: Event | null = null

   showEnlargedLogo?: boolean

   @Output() doActionDeleteLogo = new EventEmitter()
   @Output() doActionSave = new EventEmitter<string>()
   @Output() hasUnsavedChanges = new EventEmitter<boolean>()

   constructor(
      public cdr: ChangeDetectorRef,
      private sanitizer: DomSanitizer,
      private notificationService: AppNotificationService,
      private translate: TranslateService
   ) {}

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['logo']) {
         this.croppedImageFile = this.logo ? { safeUrl: this.getSafeLogo() } : undefined
      }

      if (changes['isLoadingLogo'] && changes['isLoadingLogo'].previousValue == true && changes['isLoadingLogo'].currentValue == null) {
         this.resetCropper()
      }
   }

   onButtonSave() {
      const base64Image = this.getImage()
      if (base64Image) this.doActionSave.emit(base64Image)
   }

   onButtonDelete() {
      const dialogRef = $app.core.dialog.open(AppDialogDeleteComponent, {
         data: {
            dialogTitle: 'Logo'
         },
         width: '550px',
         disableClose: true
      })

      dialogRef.afterClosed().subscribe((confirm) => {
         if (confirm) {
            this.doActionDeleteLogo.emit()
            this.resetCropper()
         }
      })
   }

   fileChangeEvent(event: any): void {
      if (event?.target?.files && event?.target?.files[0]) {
         if (!['image/jpeg', 'image/png', 'image/gif'].includes(event.target.files[0].type)) {
            this.resetCropper()
            this.notificationService.notify(NotificationType.Warn, undefined, this.translate.instant('error_logo_file_type'))
            return
         }

         if (event.target.files[0].size > 2000000) {
            // 2MB
            this.resetCropper()
            this.notificationService.notify(NotificationType.Warn, undefined, this.translate.instant('error_logo_file_size'))
            return
         }

         this.imageChangedEvent = event
         this.imageLoaded()
         this.hasUnsavedChanges.emit(true)
      }
   }

   resetImage() {
      this.scale = 1
      this.transform = {}
   }

   imageLoaded() {
      this.showCropper = true
      this.cdr.detectChanges()
   }

   imageCropped(event: ImageCroppedEvent) {
      if (!this.showCropper) return
      if (event.base64) {
         this.croppedImageFile = { content: event.base64 }
         this.cdr.detectChanges()
      }
   }

   zoom(type: 'in' | 'out') {
      switch (type) {
         case 'in':
            this.scale += 0.1
            break
         case 'out':
            this.scale -= 0.1
            break
      }

      this.transform = {
         ...this.transform,
         scale: this.scale
      }
   }

   removeImage() {
      this.resetCropper()
   }

   getSafeLogo(): SafeResourceUrl {
      if (!this.logo) return ''
      return this.sanitizer.bypassSecurityTrustResourceUrl(this.logo)
   }

   private resetCropper() {
      this.showCropper = false
      this.resetImage()
      this.croppedImageFile = this.logo ? { safeUrl: this.getSafeLogo() } : undefined
   }

   private getImage() {
      return this.croppedImageFile?.content
   }
}
